import { useState } from "react";
import { inputClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  COLUMN_BAND_LABEL,
  COLUMN_BANDS,
  COLUMN_CALC_OPTIONS,
  columnKeyFromLabel,
  columnsInBand,
  fieldsForColumnCalc,
  isFormulaKind,
  type ColumnCalcKind,
} from "@/lib/manufacturing/cogsColumnEdit";
import type { HaulRateBasis } from "@/lib/manufacturing/haulRateEvaluate";
import {
  createManufacturingPricingLayerColumn,
  updateManufacturingPricingLayerColumn,
} from "@/lib/manufacturing/pricingLayersClient";
import type { MfgPricingLayerBand, MfgPricingLayerColumn, MfgPricingLayerGridRow } from "@/lib/manufacturing/types";
import { FormulaFields, parsedRate } from "./ManufacturingPricingColumnEditors";
import { PricingColumnProductScope } from "./PricingColumnProductScope";

export function AddPricingColumnForm({
  locked,
  columns,
  rows,
  onError,
  onAdded,
}: {
  locked: boolean;
  columns: MfgPricingLayerColumn[];
  rows: MfgPricingLayerGridRow[];
  onError: (message: string | null) => void;
  onAdded: () => Promise<void>;
}) {
  const [band, setBand] = useState<MfgPricingLayerBand>("cogs");
  const [label, setLabel] = useState("");
  const [calcKind, setCalcKind] = useState<ColumnCalcKind>("add_usd");
  const [basis, setBasis] = useState<HaulRateBasis>("building_sq_ft");
  const [rate, setRate] = useState("");
  const [productsOnly, setProductsOnly] = useState(false);
  const [limitProducts, setLimitProducts] = useState(false);
  const [recipeIds, setRecipeIds] = useState<string[]>([]);
  const [leaveOffGrid, setLeaveOffGrid] = useState(false);
  const formula = isFormulaKind(calcKind);
  const rateUsd = basis === "none" ? 0 : parsedRate(rate);
  const rateInvalid = basis !== "none" && rate.trim() !== "" && rateUsd === null;
  const scopeInvalid = formula && limitProducts && (recipeIds.length === 0 || rateUsd === null);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-2">
        <label className="block space-y-1 text-sm">
          <span className="text-gray-600 dark:text-zinc-400">Add to band</span>
          <select
            className={inputClass}
            value={band}
            onChange={(e) => setBand(e.target.value as MfgPricingLayerBand)}
            disabled={locked}
          >
            {COLUMN_BANDS.map((id) => (
              <option key={id} value={id}>
                {COLUMN_BAND_LABEL[id]}
              </option>
            ))}
          </select>
        </label>
        <label className="block min-w-[10rem] flex-1 space-y-1 text-sm">
          <span className="text-gray-600 dark:text-zinc-400">New column label</span>
          <input
            className={inputClass}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Escort"
            disabled={locked}
          />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-gray-600 dark:text-zinc-400">Calculation</span>
          <select
            className={inputClass}
            value={calcKind}
            onChange={(e) => setCalcKind(e.target.value as ColumnCalcKind)}
            disabled={locked}
          >
            {COLUMN_CALC_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {formula ? (
          <>
            <FormulaFields basis={basis} rate={rate} locked={locked} onBasis={setBasis} onRate={setRate} />
            <label className="flex items-center gap-1 pb-2 text-xs text-gray-600 dark:text-zinc-400">
              <input
                type="checkbox"
                checked={productsOnly}
                onChange={(e) => setProductsOnly(e.target.checked)}
                disabled={locked}
              />
              Products only
            </label>
          </>
        ) : null}
        <label
          className="flex items-center gap-1 pb-2 text-xs text-gray-600 dark:text-zinc-400"
          title="The product drill still prices this column. The COGS totals do not."
        >
          <input
            type="checkbox"
            checked={leaveOffGrid}
            onChange={(e) => setLeaveOffGrid(e.target.checked)}
            disabled={locked}
          />
          Leave off the grid
        </label>
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={locked || !label.trim() || rateInvalid || scopeInvalid}
          onClick={() => {
            const name = label.trim();
            const fields = fieldsForColumnCalc(calcKind, band);
            void (async () => {
              onError(null);
              const created = await createManufacturingPricingLayerColumn({
                band,
                key: columnKeyFromLabel(name),
                label: name,
                ...fields,
                sort_order: (columnsInBand(columns, band).at(-1)?.sort_order ?? 0) + 10,
                is_active: !leaveOffGrid,
              });
              if (!created.ok) {
                onError(created.error);
                return;
              }
              if (calcKind === "markup") {
                const marked = await updateManufacturingPricingLayerColumn({
                  column_id: created.data.column.id,
                  rate_kind: "markup",
                });
                if (!marked.ok) {
                  onError(marked.error);
                  await onAdded();
                  return;
                }
              } else if (formula) {
                const applied = await updateManufacturingPricingLayerColumn({
                  column_id: created.data.column.id,
                  applies_to: productsOnly ? "products" : "all",
                  ...(rateUsd !== null
                    ? {
                        calc: { basis, rate_usd: rateUsd },
                        applies_recipe_ids: limitProducts ? recipeIds : null,
                      }
                    : {}),
                });
                if (!applied.ok) {
                  onError(applied.error);
                  await onAdded();
                  return;
                }
                const missing = applied.data.missing_size_count ?? 0;
                if (missing > 0) {
                  onError(`${missing} products have no size, so this column was left blank for them.`);
                }
              }
              setLabel("");
              setRate("");
              setRecipeIds([]);
              setLimitProducts(false);
              setLeaveOffGrid(false);
              await onAdded();
            })();
          }}
        >
          Add column
        </button>
      </div>
      {formula ? (
        <PricingColumnProductScope
          rows={rows}
          productsOnly={productsOnly}
          enabled={limitProducts}
          selectedIds={recipeIds}
          locked={locked}
          onEnabled={setLimitProducts}
          onSelectedIds={setRecipeIds}
        />
      ) : null}
    </div>
  );
}
